import React from 'react'
import { StyleSheet, View } from 'react-native'

import { XStack } from '@zet/design-system'
import Animated, {
  useAnimatedStyle,
  useFrameCallback,
  useSharedValue,
} from 'react-native-reanimated'

// @see: https://docs.swmansion.com/react-native-reanimated/examples/marquee/
const MeasureElement = ({ onLayout, children }) => (
  <Animated.ScrollView
    horizontal
    style={marqueeStyles.hidden}
    pointerEvents="box-none"
  >
    <View onLayout={(ev) => onLayout(ev.nativeEvent.layout.width)}>
      {children}
    </View>
  </Animated.ScrollView>
)

const TranslatedElement = ({ index, children, offset, childrenWidth }) => {
  const animatedStyle = useAnimatedStyle(() => {
    return {
      left: (index - 1) * childrenWidth,
      transform: [
        {
          translateX: -offset.value,
        },
      ],
    }
  })
  return (
    <Animated.View style={[styles.animatedStyle, animatedStyle]}>
      {children}
    </Animated.View>
  )
}

const getIndicesArray = (length) => Array.from({ length }, (_, i) => i)

const Cloner = ({ count, renderChild }) => (
  <>{getIndicesArray(count).map(renderChild)}</>
)

const ChildrenScroller = ({
  duration,
  childrenWidth,
  parentWidth,
  reverse,
  children,
}) => {
  const offset = useSharedValue(0)
  const coeff = useSharedValue(reverse ? 1 : -1)

  React.useEffect(() => {
    coeff.value = reverse ? 1 : -1
  }, [reverse])

  useFrameCallback((i) => {
    // prettier-ignore
    offset.value += (coeff.value * ((i.timeSincePreviousFrame ?? 1) * childrenWidth)) / duration;
    offset.value = offset.value % childrenWidth
  }, true)

  const count = Math.round(parentWidth / childrenWidth) + 3
  const renderChild = (index) => (
    <TranslatedElement
      key={`clone-${index}`}
      index={index}
      offset={offset}
      childrenWidth={childrenWidth}
    >
      {children}
    </TranslatedElement>
  )

  return <Cloner count={count} renderChild={renderChild} />
}

export const Marquee = ({
  duration = 9000,
  reverse = false,
  children,
  className,
}) => {
  const [parentWidth, setParentWidth] = React.useState(0)
  const [childrenWidth, setChildrenWidth] = React.useState(0)

  return (
    <XStack
      className={className}
      onLayout={(ev) => {
        setParentWidth(ev.nativeEvent.layout.width)
      }}
      pointerEvents="box-none"
    >
      <View style={marqueeStyles.row} pointerEvents="box-none">
        <MeasureElement onLayout={setChildrenWidth}>{children}</MeasureElement>

        {childrenWidth > 0 && parentWidth > 0 && (
          <ChildrenScroller
            duration={duration}
            parentWidth={parentWidth}
            childrenWidth={childrenWidth}
            reverse={reverse}
          >
            {children}
          </ChildrenScroller>
        )}
      </View>
    </XStack>
  )
}

const marqueeStyles = StyleSheet.create({
  hidden: { opacity: 0, zIndex: -1 },
  row: { flexDirection: 'row', overflow: 'hidden' },
})

const styles = StyleSheet.create({
  animatedStyle: {
    position: 'absolute',
  },
})
